import React, { useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  CardRoot,
  CloseButton,
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  HStack,
  PrimaryButton,
  RadioGroupItem,
  RadioGroupItemControl,
  RadioGroupItemHiddenInput,
  RadioGroupRoot,
  Stack,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import type { ListOrganizationDealPerFrameResponse } from '@enechain/eposition-proto/bffportal/v1/report_service_pb'

import generateDeliveryPeriodText from '~/pages/ContractList/utils/deliveryPeriodFormatter'

import type { ContractListFilterState } from '../../hooks/useContractListFilter'
import { usePerFrameContractCsvReportExport } from '../../hooks/usePerFrameContractCsvReportExport'
import type { ContractDealReportModel } from '../../models/contractDealReport'

type PerFrameEnergyExportModalProps = {
  open: boolean
  onClose: () => void
  data?: ContractDealReportModel[]
  perFrameData?: ListOrganizationDealPerFrameResponse
  filterState: ContractListFilterState
}

export const PerFrameContractsExportModal: React.FC<
  PerFrameEnergyExportModalProps
> = ({ open, onClose, data, perFrameData, filterState }) => {
  const { t } = useTranslation(['page', 'common'])
  const [formatType, setFormatType] = useState<'combined' | 'split'>('split')

  const { exportCsv, isLoading } = usePerFrameContractCsvReportExport(
    perFrameData,
    data ?? [],
    filterState,
  )

  const handleExport = async (): Promise<void> => {
    await exportCsv({ splitArea: formatType === 'split' })
    onClose()
  }

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose()
        }
      }}
      size="lg"
      placement="center"
    >
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent>
          <DialogHeader>
            {t('page:contract_list.table.export_modal.title', {
              value: t('page:contract_list.table.export_menu.per_frame_energy'),
            })}
          </DialogHeader>
          <DialogCloseTrigger asChild>
            <CloseButton size="sm" />
          </DialogCloseTrigger>
          <DialogBody>
            <Stack gap="4">
              <Text textStyle="textMd">
                {t(
                  'page:contract_list.table.export_modal.per_frame_energy_description',
                  {
                    count: data?.length ?? 0,
                  },
                )}
              </Text>
              <Stack gap="2">
                <Text textStyle="headerMd">
                  {t('page:contract_list.filter.filter_conditions')}
                </Text>
                <CardRoot
                  borderWidth="1px"
                  borderColor="Border.midEmphasis"
                  backgroundColor="Surface.secondary"
                  padding="4"
                  boxShadow="none"
                  gap="2"
                >
                  <HStack
                    gap="4"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Text textStyle="headerXs">
                      {t('page:contract_list.filter.delivery_period')}
                    </Text>
                    <Text textStyle="textMd">
                      {generateDeliveryPeriodText(filterState)}
                    </Text>
                  </HStack>
                  <HStack
                    gap="4"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                  >
                    <Text textStyle="headerXs">
                      {t('page:contract_list.filter.display_conditions_label')}
                    </Text>
                    <Text textStyle="textMd">
                      {filterState.excludeSettled
                        ? t('page:contract_list.filter.exclude_settled')
                        : t('page:contract_list.filter.include_settled')}
                    </Text>
                  </HStack>
                </CardRoot>
              </Stack>

              <Stack gap="2">
                <Text textStyle="headerMd">
                  {t('page:contract_list.table.export_modal.csv_format')}
                </Text>
                <RadioGroupRoot
                  value={formatType}
                  size="lg"
                  onValueChange={(details) =>
                    setFormatType(details.value as 'combined' | 'split')
                  }
                >
                  <Stack gap="4">
                    <CardRoot
                      borderWidth="1px"
                      borderColor="Border.midEmphasis"
                      boxShadow="none"
                      padding="4"
                      backgroundColor={
                        formatType === 'split'
                          ? 'Press.lowEmphasis'
                          : 'transparent'
                      }
                      cursor="pointer"
                      onClick={() => setFormatType('split')}
                    >
                      <HStack gap="4" align="center">
                        <RadioGroupItem value="split">
                          <RadioGroupItemHiddenInput />
                          <RadioGroupItemControl />
                        </RadioGroupItem>
                        <Stack gap="2">
                          <Text textStyle="headerMd">
                            {t(
                              'page:contract_list.table.export_modal.area_split_format',
                            )}
                          </Text>
                          <Text textStyle="textMd">
                            {t(
                              'page:contract_list.table.export_modal.area_split_description',
                            )}
                          </Text>
                          <img
                            src="/images/csv_sample_per_areas.png"
                            alt="Area split format example"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                        </Stack>
                      </HStack>
                    </CardRoot>
                    <CardRoot
                      borderWidth="1px"
                      borderColor="Border.midEmphasis"
                      boxShadow="none"
                      padding="4"
                      backgroundColor={
                        formatType === 'combined'
                          ? 'Press.lowEmphasis'
                          : 'transparent'
                      }
                      cursor="pointer"
                      onClick={() => setFormatType('combined')}
                    >
                      <HStack gap="4" align="center">
                        <RadioGroupItem value="combined">
                          <RadioGroupItemHiddenInput />
                          <RadioGroupItemControl />
                        </RadioGroupItem>
                        <Stack align="flex-start" gap="2">
                          <Text textStyle="headerMd">
                            {t(
                              'page:contract_list.table.export_modal.all_areas_format',
                            )}
                          </Text>
                          <Text textStyle="textMd">
                            {t(
                              'page:contract_list.table.export_modal.all_areas_description',
                            )}
                          </Text>
                          <img
                            src="/images/csv_sample_all_area.png"
                            alt="All areas combined format example"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none'
                            }}
                          />
                        </Stack>
                      </HStack>
                    </CardRoot>
                  </Stack>
                </RadioGroupRoot>
              </Stack>
            </Stack>
          </DialogBody>
          <DialogFooter>
            <HStack gap="4">
              <TertiaryButton onClick={onClose} disabled={isLoading}>
                {t('common:action.negative.cancel')}
              </TertiaryButton>
              <PrimaryButton onClick={handleExport} loading={isLoading}>
                {t('common:action.positive.start_download')}
              </PrimaryButton>
            </HStack>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
